import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import clsx from 'clsx'
import Icon from '../../../atoms/Icon'
import { COLUMN_CATALOG_MAP } from '../lib/constants'
import useColumnBuilderStyles from './columnBuilderStyles'
import { DRAG_TYPE, getDateHint } from './columnBuilderUtils'

function DraggableColumnRow ({ column, index, isSelected, moveItem, onSelect, onRemove }) {
  const classes = useColumnBuilderStyles()
  const ref = useRef(null)
  const catalog = COLUMN_CATALOG_MAP[column.columnType]
  const dateHint = getDateHint(column.options)
  const displayLabel = column.label ?? catalog?.label ?? column.columnType
  const typeHint = catalog?.label && displayLabel !== catalog.label ? catalog.label : null

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={clsx(classes.columnRow, {
        [classes.columnRowSelected]: isSelected,
        [classes.columnRowDragging]: isDragging
      })}
      onClick={() => onSelect(column.id)}
    >
      <Icon name='dragIndicator' customSize={14} additionalClasses={classes.dragHandle} />
      <div className={classes.columnLabelGroup}>
        <span className={classes.columnLabel}>{displayLabel}</span>
        {typeHint && <span className={classes.columnTypeHint}>{typeHint}</span>}
      </div>
      {dateHint ? <span className={classes.columnDateHint}>{dateHint}</span> : null}
      <span
        className={classes.removeBtn}
        onClick={(e) => { e.stopPropagation(); onRemove(column.id) }}
      >
        <Icon name='close' customSize={14} />
      </span>
    </div>
  )
}

DraggableColumnRow.propTypes = {
  column: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  moveItem: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}

export default DraggableColumnRow
